import React, { useState } from "react";
import { Check } from "lucide-react";
import DocSidebar from "../components/DocSidebar";
import CodeBlock from "../components/CodeBlock";
import MethodBadge from "../components/MethodBadge";
import Keyword from "../components/Keyword";

const sidebarItems = [
  { id: "stt-overview-nav", label: "Getting Started", type: "category" },
  { id: "stt-overview", label: "Overview", type: "item", indent: true },

  { id: "stt-getting-started", label: "Getting Started", type: "heading" },
  { id: "stt-base-url", label: "Base URL", type: "item", indent: true },
  { id: "stt-auth-header", label: "Authentication Header", type: "item", indent: true },

  { id: "stt-api", label: "Speech-to-Text API", type: "heading" },
  { id: "stt-endpoint", label: "Endpoint (/with-api-key)", type: "item", indent: true },
  { id: "stt-full-endpoint-url", label: "Full Endpoint URL", type: "item", indent: true },
  { id: "stt-request-header", label: "Request Headers", type: "item", indent: true },
  { id: "stt-request-body", label: "Request Body (Multipart Form)", type: "item", indent: true },

  { id: "stt-language-reference", label: "Language Reference", type: "heading" },
  { id: "stt-supported-languages", label: "Supported Languages", type: "item", indent: true },
  { id: "stt-audio-formats", label: "Supported Audio Formats", type: "item", indent: true },

  { id: "stt-code-examples", label: "Code Examples", type: "heading" },
  { id: "stt-python", label: "Python", type: "item", indent: true },
  { id: "stt-curl", label: "cURL", type: "item", indent: true },
  { id: "stt-javascript", label: "JavaScript (Fetch)", type: "item", indent: true },

  { id: "stt-response-format", label: "Response Format", type: "heading" },
  { id: "stt-success-response", label: "Success Response", type: "item", indent: true },
  { id: "stt-error-responses", label: "Error Responses", type: "item", indent: true },

  { id: "stt-best-practices", label: "Best Practices", type: "heading" },
  { id: "stt-next-steps", label: "Next Steps", type: "heading" },
];

const SpeechToTextTab = () => {
  const [activeSection, setActiveSection] = useState("stt-overview");

  return (
    <div className="flex flex-col lg:flex-row gap-8 max-w-[1440px] mx-auto px-2 sm:px-6 py-6 sm:py-10 relative">
      {/* Sidebar Navigation */}
      <DocSidebar
        items={sidebarItems}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      {/* Main Content */}
      <div className="flex flex-col gap-16 flex-1 lg:max-w-none xl:px-10">
        {/* Header */}
        <section id="stt-overview-nav" className="flex flex-col gap-4">
          <div className="inline-block">
            <span className="font-black text-sm uppercase tracking-[0.2em] text-[#17A257] bg-[#E7F6EC] px-3 py-1 rounded-full">
              Speech-to-Text API
            </span>
          </div>
          <div id="stt-overview" className="flex flex-col gap-4">
            <h1 className="font-black text-3xl lg:text-4xl leading-tight text-[#1D2129]">
              Quickstart Guide For Speech-to-Text (STT) API
            </h1>
            <p className="font-medium text-lg leading-relaxed text-[#7E726B]">
              The NKENNE AI Speech-to-Text API provides automatic speech recognition (ASR) tailored for African languages, accents, dialects, and audio environments using your developer API key.
            </p>
          </div>
        </section>

        {/* Getting Started Section */}
        <div className="flex flex-col gap-20">
          <section id="stt-getting-started" className="flex flex-col gap-6 w-full">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Getting Started
            </h2>

            <div className="flex flex-col gap-6">
              {/* Base URL */}
              <div id="stt-base-url" className="flex flex-col gap-2">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Base URL
                </h3>
                <div className="bg-[#F4F8F7] p-3 rounded-xl border border-[#D0E7DF] inline-block w-fit">
                  <p className="font-mono font-medium text-base text-[#1E6C56]">
                    https://developers-api.nkenne.ai
                  </p>
                </div>
              </div>

              {/* Authentication Header */}
              <div id="stt-auth-header" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Authentication Header
                </h3>
                <p className="font-['Inter'] font-medium text-base text-[#7E726B]">
                  Include your API key in the <Keyword>Authorization</Keyword> header with all transcription requests:
                </p>
                <div className="bg-[#1D2129] p-4 rounded-xl font-mono text-sm text-white overflow-hidden w-fit">
                  <span className="text-[#BC39B7]">Authorization</span>:{" "}
                  <span className="text-[#17A257]">YOUR_API_KEY</span>
                </div>
              </div>
            </div>
          </section>

          {/* Speech-to-Text API */}
          <section id="stt-api" className="flex flex-col gap-6">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Speech-to-Text API
            </h2>

            <div className="flex flex-col gap-6">
              {/* Endpoint */}
              <div id="stt-endpoint" className="flex flex-col gap-5">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Endpoint
                </h3>
                <div className="flex items-center gap-3">
                  <MethodBadge method="POST" />
                  <span className="font-mono text-sm font-semibold text-[#1D2129]">
                    /api/stt/v1/{`{language}`}/with-api-key
                  </span>
                </div>
              </div>

              {/* Full Endpoint URL */}
              <div id="stt-full-endpoint-url" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Full Endpoint URL
                </h3>
                <p className="font-mono text-sm font-medium text-[#1E6C56] bg-[#F4F8F7] p-3 rounded-xl border border-[#D0E7DF] inline-block w-fit">
                  https://developers-api.nkenne.ai/api/stt/v1/{`{language}`}/with-api-key
                </p>
              </div>

              {/* Request Headers */}
              <div id="stt-request-header" className="flex flex-col gap-4">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Request Headers
                </h3>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base text-[#000000]">
                          Header
                        </th>
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base text-[#000000]">
                          Required
                        </th>
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base text-[#000000]">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#E0E4E2]">
                        <td className="p-3">
                          <Keyword>Authorization</Keyword>
                        </td>
                        <td className="p-3">
                          <Check className="w-5 h-5 text-[#17A257]" />
                        </td>
                        <td className="p-3 text-sm text-[#000000]">
                          YOUR_API_KEY
                        </td>
                      </tr>
                      <tr>
                        <td className="p-3">
                          <Keyword>Content-Type</Keyword>
                        </td>
                        <td className="p-3">
                          <Check className="w-5 h-5 text-[#17A257]" />
                        </td>
                        <td className="p-3 text-sm">
                          <Keyword color="#0070C1">multipart/form-data</Keyword>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Body (Multipart) */}
              <div id="stt-request-body" className="flex flex-col gap-4">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Request Body (Multipart Form-Data)
                </h3>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Field</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Type</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Required</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="p-3"><Keyword color="#BC39B7">audio_file</Keyword></td>
                        <td className="p-3 text-sm font-mono text-gray-600">File / Binary</td>
                        <td className="p-3"><Check className="w-5 h-5 text-[#17A257]" /></td>
                        <td className="p-3 text-sm">The recorded audio file to transcribe (max 25MB).</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* Language Reference */}
          <section id="stt-language-reference" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Language Reference
            </h2>

            <div className="flex flex-col gap-8">
              {/* Supported Languages */}
              <div id="stt-supported-languages" className="flex flex-col gap-4">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Supported Languages & URL Slugs
                </h3>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Language</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Slug (URL Parameter)</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Accents & Dialects</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { lang: "Yoruba", slug: "yoruba", note: "Standard Yoruba, regional accents & tonal speech" },
                        { lang: "Swahili", slug: "swahili", note: "Standard East African Swahili" },
                        { lang: "Nigerian Pidgin", slug: "nigerian-pidgin", note: "Modern West African Nigerian Pidgin" },
                        { lang: "English", slug: "english", note: "African & International English accents" },
                      ].map((row, index) => (
                        <tr key={index} className={index < 3 ? "border-b border-[#E0E4E2]" : ""}>
                          <td className="p-3 font-medium text-sm text-[#000000]">{row.lang}</td>
                          <td className="p-3"><Keyword color="#0070C1">{row.slug}</Keyword></td>
                          <td className="p-3 text-sm text-gray-700">{row.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Supported Audio Formats */}
              <div id="stt-audio-formats" className="flex flex-col gap-4">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Supported Audio Formats
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { ext: "WAV", desc: "16-bit PCM (recommended)" },
                    { ext: "MP3", desc: "MPEG-1/2 Audio Layer III" },
                    { ext: "M4A / AAC", desc: "Advanced Audio Coding" },
                    { ext: "FLAC / OGG", desc: "Lossless / Opus compression" },
                  ].map((fmt, index) => (
                    <div key={index} className="p-4 rounded-xl border border-[#E0E4E2] bg-white text-center">
                      <p className="font-bold text-base text-[#17A257]">{fmt.ext}</p>
                      <p className="text-xs text-[#7E726B] mt-1">{fmt.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Code Examples */}
          <section id="stt-code-examples" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Code Examples
            </h2>

            <div className="flex flex-col gap-8">
              {/* Python Example */}
              <div id="stt-python" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-lg text-[#000000]">
                  Python
                </p>
                <CodeBlock
                  id="stt-python-example"
                  language="python"
                  code={`import requests

API_URL = "https://developers-api.nkenne.ai/api/stt/v1/yoruba/with-api-key"
API_KEY = "your_api_key_here"

headers = {
    "Authorization": API_KEY
}

with open("voice_sample.wav", "rb") as audio_file:
    files = {"audio_file": ("voice_sample.wav", audio_file, "audio/wav")}
    response = requests.post(API_URL, headers=headers, files=files)

response.raise_for_status()
data = response.json()

print("Transcribed Text:", data.get("text"))
print("Duration (s):", data.get("duration"))`}
                />
              </div>

              {/* cURL Example */}
              <div id="stt-curl" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  cURL
                </p>
                <CodeBlock
                  id="stt-curl-example"
                  language="bash"
                  code={`curl -X POST https://developers-api.nkenne.ai/api/stt/v1/yoruba/with-api-key \\
  -H "Authorization: YOUR_API_KEY" \\
  -F "audio_file=@/path/to/voice_sample.wav"`}
                />
              </div>

              {/* JavaScript Example */}
              <div id="stt-javascript" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  JavaScript (Fetch)
                </p>
                <CodeBlock
                  id="stt-javascript-example"
                  language="javascript"
                  code={`const formData = new FormData();
formData.append("audio_file", audioBlobOrFileInput);

const response = await fetch(
  "https://developers-api.nkenne.ai/api/stt/v1/yoruba/with-api-key",
  {
    method: "POST",
    headers: {
      "Authorization": "YOUR_API_KEY"
    },
    body: formData
  }
);

const data = await response.json();
console.log("Transcribed text:", data.text);`}
                />
              </div>
            </div>
          </section>

          {/* Response Format */}
          <section id="stt-response-format" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Response Format
            </h2>

            <div className="flex flex-col gap-10">
              {/* Success Response */}
              <div id="stt-success-response" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Success Response (200 OK)
                </h3>
                <CodeBlock
                  id="stt-success-response-code"
                  language="json"
                  code={`{
  "text": "Ẹ ku aarọ o gbogbo ile, bawo ni nkan?",
  "duration": 3.45,
  "language": "yoruba"
}`}
                />
              </div>

              {/* Error Responses */}
              <div id="stt-error-responses" className="flex flex-col gap-6">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Error Responses
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Invalid API Key */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Unauthorized (401)
                    </p>
                    <CodeBlock
                      id="stt-error-401"
                      language="json"
                      code={`{
  "error": "Unauthorized: Invalid API Key"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      API key is missing or invalid. Verify your key in Developer Tools.
                    </p>
                  </div>

                  {/* Missing File */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Bad Request (400)
                    </p>
                    <CodeBlock
                      id="stt-error-400"
                      language="json"
                      code={`{
  "error": "No audio_file field provided in formData"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      Ensure the form-data key is named <Keyword color="#BC39B7">audio_file</Keyword>.
                    </p>
                  </div>

                  {/* File Size Exceeded */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Payload Too Large (413)
                    </p>
                    <CodeBlock
                      id="stt-error-413"
                      language="json"
                      code={`{
  "error": "Audio file exceeds maximum size limit (25MB)"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      Split long audio recordings into segments under 25MB before uploading.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section id="stt-best-practices" className="flex flex-col gap-6">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Best Practices
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: "Audio Quality", desc: "Ensure clear speaker microphone recording with minimal background ambient noise for highest transcription accuracy." },
                { title: "Sampling Rate", desc: "Record at 16kHz or higher sample rate in mono channel format for optimal model inference." },
                { title: "Chunk Long Audios", desc: "For recordings longer than 5 minutes, segment audio files into shorter clips to improve processing speed and resilience." },
                { title: "Token Estimation", desc: "Audio token usage is calculated based on audio length. Monitor speech analytics to optimize token budgets." },
              ].map((practice, index) => (
                <div key={index} className="p-4 rounded-xl border border-[#E0E4E2] bg-white">
                  <h4 className="font-bold text-base text-[#1D2129] mb-1">{practice.title}</h4>
                  <p className="text-sm text-[#7E726B]">{practice.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Next Steps */}
          <section id="stt-next-steps" className="flex flex-col gap-6 mb-12">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Next Steps
            </h2>
            <div className="flex flex-col gap-3">
              {[
                "Test live microphone recording and file uploads in the Speech Studio",
                "Integrate voice typing and automated transcriptions into customer support flows",
                "Combine with Text-to-Text translation to translate speech into multiple languages",
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#17A257]" />
                  <p className="font-medium text-base text-[#1D2129]">{step}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SpeechToTextTab;
